<!--~
~ Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
~ to an End-User License Agreement included in a separate file named EULA.txt
~ that is distributed with this file.
-->
@if (definition) {
  <div fxLayout="column" fxLayoutAlign="start stretch" fxFlex>
    @if (shouldDisplayFilterUi) {
      <dash-gis-report-content-filter
        [definition]="definition"
        [id]="id"
      fxFlex="0 0 auto"></dash-gis-report-content-filter>
    }
    <div class="report-table-container">
      <div class="overlay loading-container" [class.loading]="dataSource.loading$ | async">
        <mat-progress-spinner mode="indeterminate" diameter="60" strokeWidth="15"></mat-progress-spinner>
        <div class="report-loading-overlay"></div>
      </div>
      @if (dataSource?.hasHtml$ | async) {
        <div class="selectable-text report-template-content"
          fxLayout="column"
          fxLayoutAlign="space-between start"
          fxFlexAlign="stretch"
          fxFlex="1 1 auto"
        [innerHTML]="dataSource?.reportHtml"></div>
      } @else {
        <form class="selectable-text tabular-data-form" fxLayout="column" fxFlex [formGroup]="dataFormGroup">
          <div class="table-container" fxFlex fxFlexAlign="stretch">
            <table mat-table [dataSource]="dataSource" matSort (matSortChange)="sortChange($event)">
              @if (mapEnabled && definition.enableZoom) {
                <ng-container [matColumnDef]="'zoom'">
                  <th mat-header-cell *matHeaderCellDef>Zoom</th>
                  <td mat-cell *matCellDef="let row">
                    <dash-gis-zoom-button
                      [zoomLayer]="definition.layerField ? row[definition.layerField] : definition.layer"
                      [zoomObjectKey]="row[definition.dbKey]"
                      [zoomObjectField]="definition.objectKey"
                    [otherZoomLayers]="definition.otherZoomLayers"></dash-gis-zoom-button>
                  </td>
                </ng-container>
              }
              @if (definition.totalFields) {
                <td mat-footer-cell *matFooterCellDef>Totals:</td>
              }
              @if (definition.averageFields) {
                <td mat-footer-cell *matFooterCellDef>Averages:</td>
              }
              @for (field of definition.fields; track field.name) {
                <ng-container [matColumnDef]="field.name">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ field.display }}</th>
                  <td mat-cell *matCellDef="let row"
                    [style.max-width]="field.maxWidth || ''"
                    [style.min-width]="field.minWidth || ''"
                    [class.no-wrap]="!field.wrap">
                    @if (tableEditActive) {
                      <dash-gis-base-field [fieldDefinition]="field" [rowData]="row"
                        [tableEditActive]="true"
                        [dataFormGroup]="dataFormGroup"
                      [dataForm]="dataForm"></dash-gis-base-field>
                    }
                    @if (!tableEditActive) {
                      <dash-gis-base-field [fieldDefinition]="field" [rowData]="row"
                      [tableEditActive]="false"></dash-gis-base-field>
                    }
                  </td>
                </ng-container>
              }
              @if (definition.rowDataFormName) {
                <ng-container [matColumnDef]="'edit'">
                  <th mat-header-cell *matHeaderCellDef>Edit</th>
                  <td mat-cell *matCellDef="let row">
                    <button mat-icon-button
                                (click)="editRowData.emit({
                                        name: definition.rowDataFormName,
                                        keys: [ row[definition.rowDataFormKeyField || definition.dbKey] ]
                                    })">
                      <mat-icon svgIcon="edit"></mat-icon>
                    </button>
                  </td>
                </ng-container>
              }
              <!-- totals -->
              @for (fieldName of columnsToDisplay; track fieldName; let i = $index) {
                <ng-container
                  [matColumnDef]="'total_' + fieldName">
                  @if (definition.totalFields) {
                    @switch (getTotalsCellDisplay(fieldName, i)) {
                      @case ('label') {
                        <td mat-footer-cell *matFooterCellDef>Totals:</td>
                      }
                      @case ('value') {
                        <td mat-footer-cell *matFooterCellDef>
                          {{ getTotalsValue$(fieldName) | async | number: '1.2' }}
                        </td>
                      }
                      @default {
                        <td mat-footer-cell *matFooterCellDef></td>
                      }
                    }
                    @if (!definition.enableZoom && i === 0) {
                      <td mat-footer-cell *matFooterCellDef>Totals:</td>
                    }
                  }
                </ng-container>
              }
              <!-- averages -->
              @for (fieldName of columnsToDisplay; track fieldName; let i = $index) {
                <ng-container
                  [matColumnDef]="'average_' + fieldName">
                  @if (definition.averageFields) {
                    @switch (getAveragesCellDisplay(fieldName, i)) {
                      @case ('label') {
                        <td mat-footer-cell *matFooterCellDef>Averages:</td>
                      }
                      @case ('value') {
                        <td mat-footer-cell *matFooterCellDef>
                          {{ getAveragesValue$(fieldName) | async | number: '1.2' }}
                        </td>
                      }
                      @default {
                        <td mat-footer-cell *matFooterCellDef></td>
                      }
                    }
                    @if (!definition.enableZoom && i === 0) {
                      <td mat-footer-cell *matFooterCellDef>Averages:</td>
                    }
                  }
                </ng-container>
              }
              <tr mat-header-row *matHeaderRowDef="columnsToDisplay; sticky: true"></tr>
              <tr mat-row *matRowDef="let rowData; columns: columnsToDisplay"></tr>
              @if (definition.totalFields) {
                <tr mat-footer-row *matFooterRowDef="totalsColumnsToDisplay"></tr>
              }
              @if (definition.averageFields) {
                <tr mat-footer-row *matFooterRowDef="averagesColumnsToDisplay"></tr>
              }
            </table>
          </div>
        </form>
      }
      @if (!tableEditActive) {
        <mat-paginator [hidePageSize]="(dataSource?.count$ | async) <= (pagingOptions$ | async)?.limit"
          [pageIndex]="(pagingOptions$ | async)?.page"
          [pageSize]="(pagingOptions$ | async)?.limit"
          [pageSizeOptions]="pageSizeOptions.values"
          [length]="dataSource?.count$ | async"
          (page)="pageChange($event)" fxFlex="0 0 auto"
          class="no-print">
        </mat-paginator>
      } @else {
        <div class="edit-actions-container">
          <div class="table-edit-actions" fxLayout="row" fxLayoutAlign="end center">
            <button mat-button (click)="tableEditSave()">Save</button>
            <button mat-button (click)="tableEditCancel()">Cancel</button>
          </div>
        </div>
      }
    </div>
  </div>
}



